/**
 * Listening (CO) audios generated on the server (D-096): the counter and the « generate the
 * missing audios » button above the question bank, then per CO question a player and a
 * « generate the audio » button. The work runs in the background; a new audio sends the
 * question back to draft so that a trainer listens to it before publishing it again.
 */

import {
  type Question,
  useAcademyQuestionsAudioStatusRetrieve,
  useAcademyQuestionsGenerateAudioCreate,
  useAcademyQuestionsGenerateMissingAudio,
} from "@agencyos/api-client";
import { Badge, Button } from "@agencyos/ui-web";
import { AudioLines, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "./co-audio.css";
import { ErrorList, useAcademyRoles, useRefreshAcademy } from "./shared";

/** While a generation runs, the counter and the list are refreshed every few seconds. */
const POLL_MS = 4000;

export function CoAudioSummary() {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const [waiting, setWaiting] = useState(false);
  const status = useAcademyQuestionsAudioStatusRetrieve({
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        return waiting && !(data?.status === 200 && data.data.without_audio === 0) ? POLL_MS : false;
      },
    },
  });
  const withAudio = status.data?.status === 200 ? status.data.data.with_audio : undefined;
  useEffect(() => {
    if (waiting && withAudio !== undefined) refresh(); // the list shows the new players
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the counter moves
  }, [withAudio]);
  const generate = useAcademyQuestionsGenerateMissingAudio({
    mutation: {
      onSuccess: (r) => {
        if (r.status !== 202) return;
        setWaiting(true);
        refresh();
      },
    },
  });
  const data = status.data?.status === 200 ? status.data.data : undefined;
  if (!data) return null;
  const queued = generate.data?.status === 202 ? generate.data.data.queued : null;

  return (
    <section className="stack co-audio" aria-labelledby="co-audio-title">
      <div className="row co-audio__head">
        <AudioLines size={18} aria-hidden="true" />
        <strong id="co-audio-title">{t("academyAudio.title")}</strong>
        <span role="status" className="muted">
          {t("academyAudio.counter", { with: data.with_audio, without: data.without_audio })}
        </span>
        {data.configured && data.mode === "sandbox" ? <Badge>{t("academyAudio.sandbox")}</Badge> : null}
      </div>
      {!data.configured ? (
        <p role="note" className="notice-warning">
          {t("academyAudio.notConfigured")}
        </p>
      ) : null}
      {canEdit ? (
        <div className="row">
          <Button
            variant="secondary"
            icon={<Wand2 size={16} aria-hidden="true" />}
            disabled={!data.configured || data.without_audio === 0 || generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? t("common.loading") : t("academyAudio.generateMissing")}
          </Button>
          {data.without_audio === 0 ? <span className="muted">{t("academyAudio.nothingMissing")}</span> : null}
          {queued !== null ? (
            <span role="status" className="ok">
              {t("academyAudio.queued", { count: queued })}
            </span>
          ) : null}
        </div>
      ) : null}
      <p className="muted">{t("academyAudio.reviewNote")}</p>
      <ErrorList error={generate.error} />
    </section>
  );
}

/** The player and the « generate the audio » button of one CO question (list row). */
export function CoAudioRowTools({ question }: { question: Question }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const generate = useAcademyQuestionsGenerateAudioCreate({ mutation: { onSuccess: refresh } });
  if (question.skill !== "co") return null;
  const queued = generate.data?.status === 202;

  return (
    <div className="stack co-audio__row">
      {question.audio_url ? <CoAudioPlayer src={question.audio_url} /> : null}
      {canEdit ? (
        <div className="row">
          <Button
            variant="ghost"
            icon={<Wand2 size={16} aria-hidden="true" />}
            disabled={generate.isPending}
            onClick={() => generate.mutate({ id: question.id })}
          >
            {question.audio_url ? t("academyAudio.regenerate") : t("academyAudio.generateOne")}
          </Button>
          {queued ? (
            <span role="status" className="ok">
              {t("academyAudio.queued", { count: 1 })}
            </span>
          ) : null}
        </div>
      ) : null}
      <ErrorList error={generate.error} />
    </div>
  );
}

export function CoAudioPlayer({ src }: { src: string }) {
  const { t } = useTranslation();
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- its script is the question's document
    <audio className="co-audio__player" controls preload="none" src={src} aria-label={t("academyAudio.listen")} />
  );
}
